import React, { FC } from 'react';
import styled from 'styled-components';
import NotifyMeModal from '@source/components/NotifyMe/NotifyMeModal';
import NotifyMeSuccessModal from '@source/components/NotifyMe/NotifyMeSuccessModal';
import useViewedCars from '@source/hooks/useViewedCars';
import useRegionConfig from '@source/hooks/useRegionConfig';
import { NOTIFY_ME_TICKET_CONTEXT } from '@source/pages/CarDetail/components/NotifyMe/constants';
import { ENV_URL } from '@source/constants/common';
import { getDeviceInfo, getSource } from '@source/utils/utils';
import useTicketService from '@source/pages/CarDetail/hooks/useTicketService';
import { IGTMObjectProps } from '@source/interface/otp';
import { formatPhoneNumberWithCountryCode } from '@source/pages/CarDetail/utils';
import useGTMEvent from '@source/hooks/useGTMEvent';
import Logger from '@source/services/Logger';
import StatusTag, { StatusTagProps } from './StatusTag';

interface NotifyMeTagWrapperProps extends Omit<StatusTagProps, 'status'> {
  carData: any;
}

const StyledStatusTagWrapper = styled.div`
  position: absolute;
  top: 11px;
  right: 13px;
`;

const NotifyMeTagWrapper: FC<NotifyMeTagWrapperProps> = (props) => {
  const { carData } = props;
  const [isShowModal, setIsShowModal] = React.useState(false);
  const [isShowSuccessModal, setIsShowSuccessModal] = React.useState(false);
  const {
    countryConfig: { country: countryCode },
  } = useRegionConfig();
  const { getViewedCars } = useViewedCars();
  const { createNotifyMeTicket } = useTicketService();
  const { pushGTMEvent } = useGTMEvent();

  const getListingPath = () => {
    let listingPath;
    if (typeof window !== 'undefined') {
      listingPath = `${window?.location?.origin}${window.location.pathname}` || `${ENV_URL[countryCode as string]}`;
    }
    return listingPath;
  };

  const handleSubmitNotifyMeTicket = async (values: any) => {
    const { phone } = values;
    const { id: slug, inventoryId, title, price, listingId } = carData;

    const viewedCars = getViewedCars(countryCode || 'sg');

    try {
      const ticketContext = NOTIFY_ME_TICKET_CONTEXT[countryCode || 'sg'] || 'buy-hunt-carro-web-sg';
      const listingPath = getListingPath();

      const sourceData = getSource(countryCode);
      const deviceInfo = getDeviceInfo('');

      const payload = {
        context: ticketContext,
        additional_data: {
          car_make_model: `[Sold] ${title}`,
          inventory_id: inventoryId,
          listing_id: listingId,
          phone,
          price,
          slug,
          viewed_cars: viewedCars,
          url: listingPath,
        },
        tracking_data: {
          ...deviceInfo,
          ...sourceData,
        },
      };

      const response = await createNotifyMeTicket({ country: countryCode || 'sg', payload });

      if (!response.error) {
        setIsShowSuccessModal(true);

        const GTMObj: IGTMObjectProps = {
          event: 'submitted_buycar_notify_me',
          page_name: 'buy_car_listing',
          listing_status: 'sold',
          listing_slug: slug,
          enhanced_conversion_data: {
            phone_number: formatPhoneNumberWithCountryCode(phone, countryCode),
          },
          ticket_id: response?.payload?.data?.data?.id,
        };

        pushGTMEvent(GTMObj);
      }
    } catch (err) {
      Logger.log(err);
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setIsShowModal(true);
  };

  const handleCloseModal = () => {
    setIsShowModal(false);
  };

  const handleCloseSuccessModal = () => {
    setIsShowSuccessModal(false);
  };

  const handleOnFinish = (values: any) => {
    setIsShowModal(false);
    setIsShowSuccessModal(true);
    handleSubmitNotifyMeTicket(values);
  };

  return (
    <StyledStatusTagWrapper>
      <StatusTag status="notifyMe" {...props} onClick={handleClick} />
      {isShowModal && <NotifyMeModal onClose={handleCloseModal} onFinish={handleOnFinish} />}
      {isShowSuccessModal && <NotifyMeSuccessModal onClose={handleCloseSuccessModal} />}
    </StyledStatusTagWrapper>
  );
};

export default NotifyMeTagWrapper;
